export interface UserJurisdiction {
  jurisdictionId: string;
  jurisdictionName: string;
  viewPort: {
    minLongitude: number;
    minLatitude: number;
    maxLongitude: number;
    maxLatitude: number;
  };
}

export interface UserDataScope {
  allowedSportIds: string[] | null;
  allowedLeagueIds: string[] | null;
  allowedOperatorIds: string[] | null;
  allowedRiskLevels: string[] | null;
  isGlobalAccess: boolean;
}

export interface UserApplication {
  applicationId: string;
  applicationName: string;
  userType: number;
  userTypeName: string;
  applicationRoles: string[];
  permissions: Record<string, string[]>;
  dataScope: UserDataScope | null;
  jurisdictions: UserJurisdiction[];
}

export interface UserInfo {
  applications: UserApplication | UserApplication[];
  operatorId?: string | null;
  name?: string;
  location?: string;
  email?: string;
  clientName?: string;
  clientId?: string;
}

// The registered applicationName for ACT KYC Console within the SSO portal's
// `applications[]` payload (confirmed against a real portal response).
export const KYC_APPLICATION_NAME = "Act.Id";

function toApplicationsArray(
  applications: UserApplication | UserApplication[] | undefined
): UserApplication[] {
  if (Array.isArray(applications)) return applications;
  if (applications) return [applications];
  return [];
}

export function getApplicationEntry(
  userInfo: UserInfo | null | undefined,
  applicationName: string
): UserApplication | undefined {
  const applications = toApplicationsArray(userInfo?.applications);
  return applications.find((app) => app.applicationName === applicationName);
}

/**
 * How many applications (site permissions) the user holds. Goes through
 * `toApplicationsArray` because a user with a single application arrives as a
 * bare object rather than a one-element array.
 */
export function getApplicationCount(userInfo: UserInfo | null | undefined): number {
  return toApplicationsArray(userInfo?.applications).length;
}

/**
 * Whether the user can return to the portal's product selector. With only one
 * application there is nothing to select between, and the portal bounces them
 * back out (logging them out on the way) — so the ACT logo must not be clickable.
 */
export function canReturnToPortal(userInfo: UserInfo | null | undefined): boolean {
  return getApplicationCount(userInfo) > 1;
}
